import { formatShortDate, formatTime } from '@/utils/datetime';
import type { Translations } from '@/i18n/locales/en';
import type { Reservation } from '@restaurant/api-client';
import { ReservationStatus } from '@restaurant/types';
import { ListItem } from 'framework7-react';
import LabelChipRow from '@/features/labels/components/LabelChipRow';
import ReservationBadge from './ReservationBadge';

interface ReservationCardProps {
  reservation: Reservation;
  /** Current-locale translations. Passed in (not via useT) because this card is
   *  invoked as a function, not rendered as a component - see Rule 9 below. */
  t: Translations;
  onTap: (reservation: Reservation) => void;
  onBadgeTap: (reservation: Reservation, targetEl: HTMLElement) => void;
  onSwipeConfirm?: (reservation: Reservation) => void;
  onSwipeReject?: (reservation: Reservation) => void;
  /** Highlight when shown alongside an inspector pane (workspace split view). */
  isSelected?: boolean;
  /** Show the reservation's date under the time. Off in date-grouped lists
   *  (the section header already carries the day); on where cards span dates,
   *  e.g. the pending queue. */
  showDate?: boolean;
}

function ReservationCard({
  reservation,
  t,
  onTap,
  onBadgeTap,
  onSwipeConfirm,
  onSwipeReject,
  isSelected = false,
  showDate = false,
}: ReservationCardProps) {
  const isPending = reservation.status === ReservationStatus.PENDING;

  const occasionLabels: Record<string, string> = t.reservations.occasion;
  const seatingLabels: Record<string, string> = t.reservations.seating;
  const sr = reservation.specialRequest;
  const tags: string[] = [];
  sr?.occasion?.forEach((o) => tags.push(occasionLabels[o] ?? o));
  sr?.seatingPreference?.forEach((s) => tags.push(seatingLabels[s] ?? s));

  // Returns a `ListItem` directly (and is INVOKED as a function at call sites,
  // not used as <ReservationCard/>) so it is a *direct* child of <List>. F7-react
  // only emits the wrapping <ul> - and its background / divider / list-reset CSS -
  // when a List's children are literal ListItem elements; a wrapper component
  // breaks that, yielding bare <li> with browser disc markers. See
  // FRONTEND_PRACTICES Rule 9. The key lives here since the call site spreads the
  // returned element into a `.map` array.
  return (
    <ListItem
      key={reservation.id}
      swipeout={isPending}
      noChevron
      className={`reservation-item${isSelected ? ' reservation-item-selected' : ''}`}
    >
      <div
        slot="inner"
        className="reservation-content"
        onClick={() => onTap(reservation)}
        style={{ cursor: 'pointer' }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          {/* Time column */}
          <div style={{ flexShrink: 0, width: 50 }}>
            <span
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: 'var(--f7-theme-color)',
                lineHeight: 1.25,
              }}
            >
              {formatTime(reservation.time)}
            </span>
            {showDate && (
              <div
                style={{
                  fontSize: 11,
                  color: '#8e8e93',
                  marginTop: 1,
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                }}
              >
                {formatShortDate(reservation.time)}
              </div>
            )}
          </div>

          {/* Info column */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                lineHeight: 1.25,
                // Wrap long names onto the next line instead of overflowing the
                // card; break a long unbroken run so it can't push past the edge.
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              {reservation.guest.name}
            </div>
            <div style={{ fontSize: 13, color: '#8e8e93', marginTop: 2, lineHeight: 1.2 }}>
              {reservation.partySize}{' '}
              {reservation.partySize === 1 ? t.reservations.guest : t.reservations.guests}
            </div>
            <LabelChipRow labels={reservation.labels ?? []} />
          </div>

          {/* Badge column */}
          <div style={{ flexShrink: 0, alignSelf: 'flex-start' }}>
            <ReservationBadge
              status={reservation.status}
              arrivalStatus={reservation.arrivalStatus}
              reservationTime={reservation.time}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onBadgeTap(reservation, e.currentTarget as HTMLElement);
              }}
            />
          </div>
        </div>

        {/* Tags (occasion + seating) span the FULL content width below the header
            row, left-aligned with the guest name (past the 50px time column +
            12px gap). */}
        {tags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, paddingLeft: 62 }}>
            {tags.map((t) => (
              <span
                key={t}
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#3a3a3c',
                  background: '#fff',
                  border: '1px solid rgba(60, 60, 67, 0.18)',
                  borderRadius: 6,
                  padding: '2px 7px',
                  lineHeight: 1.5,
                }}
              >
                {t}
              </span>
            ))}
          </div>
        )}

        {/* Internal (staff) note, shown inline on the card so staff don't have to
            open the detail sheet. A soft amber "sticky note" chip makes it stand
            out; wraps onto multiple lines for long notes. */}
        {reservation.internalNote && (
          <div style={{ marginTop: 8, paddingLeft: 62 }}>
            <div
              style={{
                display: 'flex',
                gap: 6,
                alignItems: 'flex-start',
                background: '#fff8e1',
                border: '1px solid #f0d98a',
                borderRadius: 8,
                padding: '6px 10px',
                fontSize: 12,
                color: '#7a5c00',
                lineHeight: 1.35,
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              <i className="f7-icons" style={{ fontSize: 13, flexShrink: 0, marginTop: 1 }}>
                doc_text
              </i>
              <span>{reservation.internalNote}</span>
            </div>
          </div>
        )}
      </div>

      {isPending && (
        <div slot="swipeout-actions-right">
          <button
            type="button"
            className="swipeout-accept"
            onClick={() => onSwipeConfirm?.(reservation)}
          >
            {t.reservations.swipe.accept}
          </button>
        </div>
      )}
      {isPending && (
        <div slot="swipeout-actions-left">
          <button
            type="button"
            className="swipeout-decline"
            onClick={() => onSwipeReject?.(reservation)}
          >
            {t.reservations.swipe.decline}
          </button>
        </div>
      )}
    </ListItem>
  );
}

export default ReservationCard;
